<script setup lang="ts">
import BooleanIcon from '@/components/shared/BooleanIcon.vue';
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog.vue';
import ButtonContent from '@/components/devextreme/ButtonContent.vue';
import CustomerEditPopup from '@/components/maintenance/CustomerEditPopup.vue';
import { useMaintenanceDelete } from '@/composables/useMaintenanceDelete';
import { RoleGroups } from '@/constants/Roles';
import { removedMessage } from '@/helpers/notificationMessages';
import { t } from '@/i18n';
import { dataSource, useCustomerService } from '@/services/api/customerService';
import type { ICustomerResponse } from '@/types/interfaces/MaintenanceTypes';
import Icons from '@/constants/Icons';
import DxButton from 'devextreme-vue/button';
import { DxColumn, DxDataGrid, DxItem, DxToolbar } from 'devextreme-vue/data-grid';
import { ref, shallowRef } from 'vue';

definePage({
  name: 'MaintenanceCustomers',
  meta: {
    title: 'maintenance.tabs.customers',
    roles: RoleGroups.Maintenance
  }
});

const customerGridRef = shallowRef<DxDataGrid>();
const customerEditPopupRef = ref<typeof CustomerEditPopup>();
const deleteDialogRef = ref<InstanceType<typeof DeleteConfirmationDialog>>();
const api = useCustomerService();

async function confirmDelete(data: ICustomerResponse): Promise<void> {
  const key = {
    id: data.id,
    name: data.name,
    stationCode: data.stationCode
  };

  await deleteDialogRef?.value?.show(key, `${key.stationCode} - ${key.name}`);
}

const deleteCustomer = useMaintenanceDelete({
  dataSource,
  dialog: () => deleteDialogRef.value,
  remove: ({ id }: { id: number, name: string, stationCode: string }) => api.deleteCustomer(id),
  key: ({ id }) => id,
  removedMessage: ({ name, stationCode }) => removedMessage(t('entities.customer'), name, stationCode.trim().toUpperCase())
});

async function updateData(data: ICustomerResponse, key?: number | null): Promise<void> {
  dataSource.upsert(data, key);
}
</script>

<template>
  <CustomerEditPopup ref="customerEditPopupRef" @update="updateData" />
  <DeleteConfirmationDialog ref="deleteDialogRef" :entity="$t('entities.customer')"
      @confirm="deleteCustomer" />

  <DxDataGrid id="customerssGrid" ref="customerGridRef" :data-source="dataSource">
    <DxToolbar>
      <DxItem location="before" template="title-template" />
      <DxItem location="before" template="add-button-template" />
      <DxItem location="after" template="refresh-button-template" />
      <DxItem :locate-in-menu="'auto'" location="after" name="searchPanel" />
    </DxToolbar>

    <DxColumn :sort-index="0" :sort-order="'asc'" :caption="$t('labels.station')" data-field="stationCode" width="150px" />
    <DxColumn :sort-index="1" :sort-order="'asc'" data-field="name" />
    <DxColumn cell-template="boolean-cell-template" data-field="enabled" :false-text="$t('common.no')"
        :true-text="$t('common.yes')" width="150px" />
    <DxColumn :visible="false" type="adaptive" />
    <DxColumn :alignment="'center'" :allow-filtering="false" :allow-sorting="false" caption="" cell-template="command-cell-template"
        data-field="id" name="command-column" width="100px" />

    <template #title-template>
      <span class="h3 fw-semibold">{{ $t('maintenance.maintain', { entities: $t('maintenance.tabs.customers') }) }}</span>
    </template>

    <template #add-button-template>
      <DxButton class="ms-3" type="success" @click="customerEditPopupRef?.addCustomer()">
        <ButtonContent :icon="Icons.Plus" :text="$t('common.addEntity', { entity: $t('entities.customer') })" />
      </DxButton>
    </template>

    <template #refresh-button-template>
      <DxButton type="primary" @click="customerGridRef?.instance?.refresh()">
        <ButtonContent :icon="Icons.ArrowsRotate" />
      </DxButton>
    </template>

    <template #boolean-cell-template="{ data }">
      <BooleanIcon :value="data.value" />
    </template>

    <template #command-cell-template="{ data }">
      <DxButton type="primary" @click="customerEditPopupRef?.editCustomer(data.value)">
        <ButtonContent :icon="Icons.Pencil" />
      </DxButton>
      <DxButton type="danger" @click="confirmDelete(data.data)">
        <ButtonContent :icon="Icons.Trash" />
      </DxButton>
    </template>
  </DxDataGrid>
</template>

<style scoped>

</style>